/**
 * MobileWorkStoppagesList
 *
 * Mobile card-based replacement for WorkStoppagesDataTable ("Suivi des arrêts de travail").
 *
 * A card carries what someone checks on a phone — who, which motif, the period, and where
 * the reimbursement stands. Everything else (the V-PLANNING identity block, the payments and
 * the traçabilité) is one tap away in the shared details dialog, which goes full-screen here
 * rather than existing as a second mobile-only component.
 */
import React, { useState, useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  Chip,
  Typography,
  Pagination,
  Fade,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import moment from "moment";
import Langs, { Localize } from "../../../app/lang/langs";
import {
  StatusChip, WorkStoppagePresetFilters, applyWorkStoppagePreset,
} from "../../../components/workStoppages/WorkStoppageStatus";

const TRANSITION_SMOOTH = "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)";

const PAGE_SIZE = 20;

/** Money as compta reads it. */
const formatAmount = (value) =>
  new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(Number(value || 0));

/**
 * @param {Object} props
 * @param {Array}  props.data - work stoppages list
 * @param {Function} props.onViewDetails - opens the details dialog for a card
 * @param {React.ReactNode} [props.header] - rendered under the search bar (the pending-requests panel)
 */
export default function MobileWorkStoppagesList({ data, onViewDetails, header }) {
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(1);
  const [preset, setPreset] = useState("ALL");

  const filteredData = useMemo(() => {
    const items = applyWorkStoppagePreset(data, preset);
    if (!searchText.trim()) return items;
    const terms = searchText.toLowerCase().split(" ").filter(Boolean);
    return items.filter((ws) => {
      const searchStr = [
        ws.nom,
        ws.prenom,
        ws.num_ss,
        ws.entite,
        ws.site,
        ws.team_name,
        ws.current_team_name,
        ws.motif,
        ws.comments,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return terms.every((t) => searchStr.includes(t));
    });
  }, [data, searchText, preset]);

  React.useEffect(() => {
    setPage(1);
  }, [searchText, preset]);

  const totalPages = Math.ceil(filteredData.length / PAGE_SIZE);
  const paginatedData = filteredData.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <Box
      sx={{
        px: 2,
        pt: 1.5,
        pb: 2.5,
        mt: "130px",
        height: "calc(100vh - 130px)",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Search bar */}
      <TextField
        fullWidth
        size="small"
        placeholder={Localize("Search") + "..."}
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
        sx={{
          mb: 1.5,
          "& input": { fontSize: "16px" },
          "& .MuiOutlinedInput-root": {
            borderRadius: "14px",
            bgcolor: "rgba(255,255,255,0.75)",
            backdropFilter: "blur(12px)",
            transition: TRANSITION_SMOOTH,
            "& fieldset": {
              borderColor: "rgba(0,0,0,0.06)",
              transition: TRANSITION_SMOOTH,
            },
            "&:hover fieldset": { borderColor: "rgba(0,0,0,0.15)" },
            "&.Mui-focused fieldset": {
              borderColor: "warning.main",
              boxShadow: "0 0 0 3px rgba(255,144,21,0.15)",
            },
          },
        }}
      />

      {header}

      <WorkStoppagePresetFilters rows={data} value={preset} onChange={setPreset} sx={{ mb: 1.5 }} />

      {/* Results count */}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mb: 1, px: 0.5, fontWeight: 500, letterSpacing: "0.02em", opacity: 0.7 }}
      >
        {filteredData.length} {Localize("results")}
      </Typography>

      {/* Card list */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
          pb: 2,
          scrollBehavior: "smooth",
          "&::-webkit-scrollbar": { width: 6 },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": {
            background: "rgba(0,0,0,0.12)",
            borderRadius: 3,
          },
        }}
      >
        {paginatedData.map((ws, index) => (
          <Fade in key={ws.id} timeout={200 + index * 40}>
            <Card
              elevation={0}
              onClick={() => onViewDetails && onViewDetails(ws)}
              sx={{
                mb: 1.5,
                cursor: onViewDetails ? "pointer" : "default",
                borderRadius: "16px",
                overflow: "hidden",
                bgcolor: "rgba(255,255,255,0.85)",
                backdropFilter: "blur(16px)",
                border: "1px solid rgba(0,0,0,0.06)",
                boxShadow: "0 2px 8px rgba(0,0,0,0.04), 0 1px 3px rgba(0,0,0,0.06)",
              }}
            >
              <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, fontSize: "0.9rem", letterSpacing: "-0.01em", mb: 0.75 }}
                >
                  {ws.prenom} {ws.nom}
                </Typography>

                {(ws.entite || ws.site || ws.team_name) && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mb: 0.75, opacity: 0.75, letterSpacing: "0.01em" }}
                  >
                    {[ws.entite, ws.site || ws.team_name].filter(Boolean).join(" · ")}
                  </Typography>
                )}

                {ws.current_team_name && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mb: 0.75, opacity: 0.75, letterSpacing: "0.01em" }}
                  >
                    <Langs str="WORK_STOPPAGE_CURRENT_TEAM" />: {ws.current_team_name}
                  </Typography>
                )}

                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 0.75 }}>
                  {ws.motif && (
                    <Chip
                      label={ws.motif}
                      size="small"
                      color="warning"
                      sx={{ fontSize: "0.7rem", height: 24, fontWeight: 600, borderRadius: "8px" }}
                    />
                  )}
                  <StatusChip
                    status={ws.stoppage_status === "PROLONGATION" ? "PROLONGATION" : "INITIAL"}
                    sx={{ fontSize: "0.7rem", height: 24 }}
                  />
                </Box>

                <Box sx={{ display: "flex", gap: 2.5, mt: 0.5, flexWrap: "wrap" }}>
                  {ws.start_date && (
                    <Typography variant="caption" color="text.secondary" sx={{ opacity: 0.7 }}>
                      <Langs str="START_DATE" />: {moment(ws.start_date).format("DD/MM/YYYY")}
                    </Typography>
                  )}
                  {ws.end_date && (
                    <Typography variant="caption" color="text.secondary" sx={{ opacity: 0.7 }}>
                      <Langs str="END_DATE" />: {moment(ws.end_date).format("DD/MM/YYYY")}
                    </Typography>
                  )}
                  {ws.cpam_days != null && (
                    <Typography variant="caption" color="text.secondary" sx={{ opacity: 0.7 }}>
                      <Langs str="WORK_STOPPAGE_CPAM_DAYS" />: {ws.cpam_days}
                    </Typography>
                  )}
                </Box>

                {/* Where the money stands — the reason this screen is opened on a phone. */}
                <Box sx={{ display: "flex", gap: 2.5, mt: 0.5, flexWrap: "wrap" }}>
                  <Typography variant="caption" color="text.secondary" sx={{ opacity: 0.7 }}>
                    <Langs str="WORK_STOPPAGE_CPAM_TOTAL" />: {formatAmount(ws.cpam_total)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ opacity: 0.7 }}>
                    <Langs str="WORK_STOPPAGE_PREVOYANCE_TOTAL" />: {formatAmount(ws.prevoyance_total)}
                  </Typography>
                </Box>

                {/* Whether each organisme has settled it, as chips so "en attente" stands out. */}
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.75 }}>
                  {[
                    ["CPAM", ws.cpam_reimbursement_status],
                    [Localize("WORK_STOPPAGE_PREVOYANCE"), ws.prevoyance_reimbursement_status],
                  ].filter(([, status]) => status).map(([organisme, status]) => (
                    <StatusChip
                      key={organisme}
                      status={status}
                      prefix={organisme}
                      sx={{ fontSize: "0.7rem", height: 24 }}
                    />
                  ))}
                </Box>

                {ws.comments && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      mt: 0.75,
                      display: "block",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      fontStyle: "italic",
                      opacity: 0.6,
                    }}
                  >
                    {ws.comments}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Fade>
        ))}

        {paginatedData.length === 0 && (
          <Fade in timeout={400}>
            <Box sx={{ textAlign: "center", py: 8 }}>
              <Typography variant="body2" color="text.secondary" sx={{ opacity: 0.5 }}>
                <Langs str="WORK_STOPPAGES_EMPTY" />
              </Typography>
            </Box>
          </Fade>
        )}
      </Box>

      {totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", pt: 1.5 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_e, value) => setPage(value)}
            size="small"
            color="warning"
            sx={{ "& .MuiPaginationItem-root": { borderRadius: "10px" } }}
          />
        </Box>
      )}
    </Box>
  );
}
